// The "?" beside a label. Tooltip has shipped since 4b.5 with its composition
// left to the consumer ("we don't auto-wire this composition") — and no consumer
// ever wrote it, so every opaque label in the product went unexplained. This is
// that composition, once, so a screen adds help by passing a string.
//
// HOVER IS NOT ENOUGH. A hover-only hint is invisible on a tablet and on any
// touch laptop, which is where an HR admin actually fills a plan template in.
// The trigger is therefore a real button that PINS the bubble open on click and
// additionally shows it on hover/focus — the pointer path is the shortcut, not
// the mechanism.
//
// `label` is required and has NO default on purpose. The design system carries
// no i18n runtime, so an English default renders verbatim into a Turkish screen
// — the same leak ReasonInput's "Required for reject" shipped as. A required
// prop cannot leak silently; it fails the build instead.
//
// THE BUBBLE IS PORTALED AND PLACED FROM MEASURED GEOMETRY (SEM-6247). It used
// to be `position:absolute` inside the trigger's own box, which made two
// promises the layout could not keep: its 288px max-width was measured against
// the VIEWPORT (`100vw - 32px`) rather than whatever box it actually sat in, and
// any scrolling ancestor clipped it. In the 248px-wide "Varlık Kaydet" modal the
// bubble ran 101px past the dialog edge and was then cut mid-word by
// `.nai-modal-body`'s `overflow:auto` — one geometry, two symptoms.
//
// There used to be an `align="end"` prop for this, and six call sites had
// reached for it. That is the tell: a hand-guessed constant standing in for a
// fact only layout knows, drifting the moment the screen around it changes —
// and ResourceForm never exposed it at all, so its fields had no escape at any
// price. Placement is now computed on open and on every scroll/resize while
// open, so no caller has to know where it sits.

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";
import { Portal } from "../lib/portal.tsx";
import { Tooltip } from "../Tooltip/Tooltip.tsx";

/** Gap between trigger and bubble — the old `top: calc(100% + 6px)`, kept. */
const GAP = 6;
/** Smallest distance the bubble may sit from any viewport edge. */
const GUTTER = 8;

export interface HelpHintProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** The explanation. Plain text — a tooltip may not contain interactive content. */
  readonly children: ReactNode;
  /**
   * Accessible name of the trigger, already localized — e.g. "Anahtar hakkında
   * yardım". Required: see the header note on English defaults.
   */
  readonly label: string;
}

export const HelpHint = forwardRef<HTMLSpanElement, HelpHintProps>(
  ({ children, label, className, ...rest }, ref) => {
    const id = useId();
    const tipId = `${id}-tip`;
    // TWO independent reasons to be open, never one `open` flag. A single flag
    // toggled by click reads mouseenter's own open as "already open" and closes
    // on the very first click — the pointer sequence is enter → down → click,
    // so the click always arrives second.
    const [pinned, setPinned] = useState(false);
    const [pointed, setPointed] = useState(false);
    const open = pinned || pointed;
    const wrapRef = useRef<HTMLSpanElement | null>(null);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const [placement, setPlacement] = useState<CSSProperties>({ top: 0, left: 0 });

    const close = useCallback((): void => {
      setPinned(false);
      setPointed(false);
    }, []);

    // Placed in a LAYOUT effect, so the corrected position is committed before
    // the browser paints — the bubble is never seen at its provisional origin.
    // The bubble's own size is read AFTER it renders: `position:fixed` makes its
    // width independent of where it is placed, so one measurement is enough.
    useLayoutEffect(() => {
      if (!open) return undefined;
      const place = (): void => {
        const trigger = wrapRef.current;
        const bubble = bubbleRef.current;
        if (trigger === null || bubble === null) return;
        const t = trigger.getBoundingClientRect();
        const b = bubble.getBoundingClientRect();

        // Slide, don't mirror. Pinning the bubble's far edge to the trigger (the
        // old `align="end"`) throws away horizontal room that is still there;
        // clamping keeps it start-aligned until the edge actually forces a
        // shift, and never leaves it narrower than it had to be.
        const left = Math.max(
          GUTTER,
          Math.min(t.left, window.innerWidth - GUTTER - b.width),
        );

        // Flip above only when below does not fit AND above does. A bubble that
        // flipped into an equally short space above would just clip upwards —
        // when neither side fits, staying below at least keeps the first line
        // next to the "?" that opened it.
        const below = t.bottom + GAP;
        const fitsBelow = below + b.height <= window.innerHeight - GUTTER;
        const fitsAbove = t.top - GAP - b.height >= GUTTER;
        setPlacement({ top: !fitsBelow && fitsAbove ? t.top - GAP - b.height : below, left });
      };

      place();
      // Capture phase: a modal body, a table, or any inner scroller moves the
      // trigger without the window ever scrolling, and those events do not
      // bubble.
      window.addEventListener("scroll", place, true);
      window.addEventListener("resize", place);
      return (): void => {
        window.removeEventListener("scroll", place, true);
        window.removeEventListener("resize", place);
      };
    }, [open]);

    // A pinned bubble stays until dismissed, so it needs an outside-click exit;
    // an unpinned one closes on blur/mouseleave and never reaches this.
    useEffect(() => {
      if (!pinned) return undefined;
      const onDocPointerDown = (event: PointerEvent): void => {
        if (!wrapRef.current?.contains(event.target as Node)) close();
      };
      document.addEventListener("pointerdown", onDocPointerDown);
      return (): void => document.removeEventListener("pointerdown", onDocPointerDown);
    }, [pinned, close]);

    return (
      <span
        ref={(node): void => {
          wrapRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref !== null) ref.current = node;
        }}
        data-component="help-hint"
        className={cx("nai-help-hint", className)}
        {...rest}
      >
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          aria-describedby={open ? tipId : undefined}
          className="nai-help-hint-trigger"
          onClick={(): void => setPinned((prev) => !prev)}
          onMouseEnter={(): void => setPointed(true)}
          onMouseLeave={(): void => setPointed(false)}
          onFocus={(): void => setPointed(true)}
          onBlur={(): void => setPointed(false)}
          onKeyDown={(event): void => {
            if (event.key === "Escape" && open) {
              event.stopPropagation();
              close();
            }
          }}
        >
          {/* Decorative: the button's meaning is its aria-label. */}
          <span aria-hidden="true">?</span>
        </button>
        {/* Portaled to the document, not rendered here: no ancestor's
            `overflow` can clip it and no ancestor's width can squeeze it. The
            outside-click handler above tests `wrapRef`, which no longer
            contains the bubble — correct, because a click on a tooltip is a
            click outside the trigger and should dismiss it. */}
        {open && (
          <Portal>
            <Tooltip
              ref={bubbleRef}
              id={tipId}
              open
              className="nai-help-hint-bubble"
              style={placement}
            >
              {children}
            </Tooltip>
          </Portal>
        )}
      </span>
    );
  },
);
HelpHint.displayName = "HelpHint";
